import type { AbstractIntlMessages } from "use-intl";
import type { AppLocale } from "./routing";

/**
 * Message catalogs, one JSON file per feature namespace per locale
 * (messages/{locale}/{namespace}.json) so a translator working on one
 * feature touches one file. Each locale is its own lazily loaded chunk —
 * a visitor only ever downloads the language they're using.
 *
 * The admin CMS namespace (the largest file by far) is NOT part of the catalog every page waits for:
 * students never need it, so only the admin layout loads it (loadAdminMessages).
 */
const catalogs = import.meta.glob<{ default: AbstractIntlMessages }>("./messages/*/*.json");

const ADMIN_NAMESPACE = "admin";

const cache = new Map<string, Promise<AbstractIntlMessages>>();

function loadNamespaces(locale: AppLocale, admin: boolean): Promise<AbstractIntlMessages> {
  const key = `${locale}:${admin}`;
  let pending = cache.get(key);
  if (!pending) {
    const prefix = `./messages/${locale}/`;
    pending = Promise.all(
      Object.entries(catalogs)
        .filter(([file]) => file.startsWith(prefix))
        .map(([file, load]) => [file.slice(prefix.length, -".json".length), load] as const)
        .filter(([namespace]) => (namespace === ADMIN_NAMESPACE) === admin)
        .map(async ([namespace, load]) => [namespace, (await load()).default] as const),
    ).then((entries) => Object.fromEntries(entries));
    cache.set(key, pending);
  }
  return pending;
}

/** Every namespace except the admin CMS one — what each page needs before it can render. */
export function loadMessages(locale: AppLocale): Promise<AbstractIntlMessages> {
  return loadNamespaces(locale, false);
}

/** Just the admin CMS namespace, loaded on top of loadMessages() by the admin layout. */
export function loadAdminMessages(locale: AppLocale): Promise<AbstractIntlMessages> {
  return loadNamespaces(locale, true);
}

// Dev only: an edited message file would otherwise leave the already-loaded
// catalog in memory (cache above + LocaleLayout state), so new keys show up
// as raw "namespace.key" strings until a manual reload. Reload instead.
if (import.meta.hot) {
  import.meta.hot.accept(() => window.location.reload());
}
